import { Accordion, Badge, Button, Divider, Group, Loader, ScrollArea, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { HiMagnifyingGlass } from 'react-icons/hi2';
import AppIcon from '@/components/AppIcon.tsx';
import StepExecutionLogs from '@/pages/Run/components/StepExecutionLogs/StepExecutionLogs.tsx';
import { getTerminalReasonPresentation } from '@/utils/terminalReasonPresentation.ts';
import StepExecutionMetadataPanel from '@/pages/Run/components/StepExecutionMetadataPanel.tsx';
import StepRunConnectionsPanel from '@/pages/Run/components/StepRunConnectionsPanel.tsx';
import StepDebugPreviewModal from '@/pages/Run/components/StepDebugPreviewModal.tsx';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import useRetrieveStepRunHistory from '@/hooks/api/runs/useRetrieveStepRunHistory.ts';
import useIsAdmin from '@/hooks/useIsAdmin.ts';
import { AutomationStep, AutomationStepMap } from '@/types/automationSteps.ts';
import { Run } from '@/types/runs.ts';
import { ExecutionStatus } from '@/types/statuses.ts';
import { StepRun, StepRunHistory } from '@/types/stepExecutions.ts';
import { formatCalendarStyle } from '@/utils/datetime.ts';
import { getStepExecutionTiming } from '@/utils/stepExecutionTiming.ts';

type Props = {
  opened: boolean;
  onClose: () => void;
  run: Run;
  step: AutomationStep;
  stepRun?: StepRun;
  stepsById: AutomationStepMap;
};

const StepRunHistoryModal = ({ opened, onClose, run, step, stepRun, stepsById }: Props) => {
  const { data, isPending, isError } = useRetrieveStepRunHistory({
    runId: run.id,
    stepId: step.step_id,
    fullPath: stepRun?.full_path,
    stepRunId: stepRun?.id,
    enabled: opened,
  });

  return (
    <StyledModal
      opened={opened}
      onClose={onClose}
      title={`${step.name} - History`}
      centered
      size="xl"
      withCloseButton
      scrollType="none"
    >
      <ScrollArea h="calc(90vh - 120px)" scrollbars="y" offsetScrollbars="y">
        <Stack px="xl" pt="lg" pb="4rem" gap={0}>
          {isPending && (
            <Group gap="xs">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading step run history
              </Text>
            </Group>
          )}

          {isError && (
            <Text size="sm" c="red">
              Unable to load step run history.
            </Text>
          )}

          {data && <StepRunHistoryContent data={data} run={run} step={step} stepsById={stepsById} />}
        </Stack>
      </ScrollArea>
    </StyledModal>
  );
};

const StepRunHistoryContent = ({
  data,
  run,
  step,
  stepsById,
}: {
  data: StepRunHistory;
  run: Run;
  step: AutomationStep;
  stepsById: AutomationStepMap;
}) => {
  const defaultOpenedItem = data.attempts.find((attempt) => attempt.is_latest)?.id ?? data.attempts[0]?.id;
  const [openedItems, setOpenedItems] = useState<string[]>(defaultOpenedItem ? [defaultOpenedItem] : []);
  const [debugAttempt, setDebugAttempt] = useState<StepRun | null>(null);
  const isAdmin = useIsAdmin();

  if (data.attempts.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No step run history found for this step path.
      </Text>
    );
  }

  return (
    <Stack gap="md">
      <Group gap="xs">
        <Badge variant="light" color="blue">
          {data.total_attempt_count} attempt{data.total_attempt_count === 1 ? '' : 's'}
        </Badge>
        <Badge variant="light" color="gray">
          {data.full_path}
        </Badge>
      </Group>

      <Accordion multiple variant="separated" value={openedItems} onChange={setOpenedItems}>
        {data.attempts.map((attempt) => (
          <Accordion.Item key={attempt.id} value={attempt.id}>
            <Accordion.Control>
              <StepRunHistoryCard attempt={attempt} />
            </Accordion.Control>
            <Accordion.Panel>
              {openedItems.includes(attempt.id) && (
                <StepRunHistoryAttemptDetails
                  run={run}
                  step={step}
                  attempt={attempt}
                  stepsById={stepsById}
                  canDebug={!!isAdmin}
                  onOpenDebug={() => setDebugAttempt(attempt)}
                />
              )}
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>

      {debugAttempt && (
        <StepDebugPreviewModal
          opened
          onClose={() => setDebugAttempt(null)}
          run={run}
          step={step}
          stepRun={debugAttempt}
        />
      )}
    </Stack>
  );
};

const StepRunHistoryCard = ({ attempt }: { attempt: StepRun }) => {
  const timing = getStepExecutionTiming(attempt.step_execution);

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-start" gap="sm">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text size="sm" fw={700} truncate>
            {attempt.full_path}
          </Text>
          <Text size="11px" c="dimmed">
            Attempt {attempt.attempt_number + 1} · Step run {attempt.id} · Step execution{' '}
            {attempt.step_execution_id}
          </Text>
        </Stack>
        <Group gap={6}>
          <Badge size="sm" variant="light" color="violet">
            Attempt {attempt.attempt_number + 1}
          </Badge>
          {attempt.is_latest && (
            <Badge size="sm" variant="light" color="blue">
              Latest
            </Badge>
          )}
          <Badge size="sm" variant="light" color={statusColor(attempt.step_execution?.status)}>
            {attempt.step_execution?.status ?? 'unknown'}
          </Badge>
          <Badge size="sm" variant="light" color={attempt.used_existing_step_execution ? 'gray' : 'green'}>
            {attempt.used_existing_step_execution
              ? attempt.is_preserved_rerun_carrier
                ? 'Preserved rerun work'
                : 'Cached'
              : 'Fresh'}
          </Badge>
        </Group>
      </Group>

      <Group gap="lg" wrap="wrap">
        <Stack gap={2}>
          <Text size="xs" c="dimmed" fw={600}>
            Runtime
          </Text>
          <Text size="sm">
            {timing.durationDisplay}
            {timing.state === 'running' ? ' elapsed' : ''}
          </Text>
        </Stack>
        <Stack gap={2}>
          <Text size="xs" c="dimmed" fw={600}>
            Started
          </Text>
          <Text size="sm">{formatCalendarStyle(attempt.step_execution?.created_at)}</Text>
        </Stack>
        <Stack gap={2}>
          <Text size="xs" c="dimmed" fw={600}>
            Finished
          </Text>
          <Text size="sm">
            {timing.state === 'running' ? 'Running' : formatCalendarStyle(attempt.step_execution?.completed_at)}
          </Text>
        </Stack>
      </Group>
    </Stack>
  );
};

const StepRunHistoryAttemptDetails = ({
  run,
  step,
  attempt,
  stepsById,
  canDebug,
  onOpenDebug,
}: {
  run: Run;
  step: AutomationStep;
  attempt: StepRun;
  stepsById: AutomationStepMap;
  canDebug: boolean;
  onOpenDebug: () => void;
}) => (
  <Stack gap="md" pt="xs">
    <Divider />
    {canDebug && (
      <Group justify="flex-end">
        <Button size="xs" variant="default" onClick={onOpenDebug}>
          <Group gap={4}>
            <AppIcon Icon={HiMagnifyingGlass} size={16} />
            Debug
          </Group>
        </Button>
      </Group>
    )}
    <SectionTitle title="Logs" />
    <StepExecutionLogs
      logItemId={attempt.step_execution_id}
      stepRunId={attempt.id}
      enabled
      stepStatus={attempt.step_execution?.status}
      terminalReasonPresentation={getTerminalReasonPresentation(
        attempt.step_execution,
        attempt.full_path,
      )}
      withDivider={false}
    />
    <SectionTitle title="References" />
    <StepRunConnectionsPanel
      stepRun={attempt}
      stepsById={stepsById}
      enabled
      withDivider={false}
    />
    <SectionTitle title="Metadata" />
    <StepExecutionMetadataPanel
      run={run}
      step={step}
      stepRun={attempt}
      stepExecution={attempt.step_execution}
      enabled
      height="320px"
    />
  </Stack>
);

const SectionTitle = ({ title }: { title: string }) => (
  <Text size="11px" fw={700} c="dimmed" tt="uppercase">
    {title}
  </Text>
);

const statusColor = (status?: string | null): string => {
  if (status === ExecutionStatus.Completed) return 'green';
  if (status === ExecutionStatus.PartiallyCompleted || status === ExecutionStatus.ActiveWithErrors) {
    return 'yellow';
  }
  if (status === ExecutionStatus.Errors || status === ExecutionStatus.Cancelled) return 'red';
  if (
    status === ExecutionStatus.Active ||
    status === ExecutionStatus.Preprocessing ||
    status === ExecutionStatus.RerunPreparing ||
    status === ExecutionStatus.Rerunning
  ) {
    return 'blue';
  }
  return 'gray';
};

export default StepRunHistoryModal;
